import { GameType } from "./enume";

export interface User {
  id: number;
  name: string;
  username: string;
}

export interface GameDetails {
  id: number;
  teamA: string;
  teamB: string;
  teamC: string;
  overs: number | null;
  gameType: string;
  isGameOver: boolean;
  winner?: string; // Optional winner field for completed matches
  /** Session match-over winning runs (from API / GameMatchOver push). */
  sessionRuns?: number;
  /** Party that receives match-over settlement transactions (0 = virtual P&L). */
  settlementPartyId?: number;
  settlementPartyCode?: string;
  settlementPartyName?: string;
  /** Toggle states from backend (get game by id); used to render switches on game board */
  commission?: boolean;
  partnership?: boolean;
  partnerSoda?: boolean;
  autoRefresh?: boolean;
}

export interface Sodo {
    id: number;
    LK: string;
    team: string;
    gameId: number;
    amount: number;
    rate: number;
    party: string;
    /** From API — use when editing so party is not resolved ambiguously from display string (e.g. codes 7 vs 77). */
    partyId?: number;
    createdAt: string;
    modifiedAt?: string;
    /** User who last modified the soda, when the API sends it. */
    modifiedBy?: User;
    user: User;
    isPartnerSoda?: boolean;
    parentId?: number; // ID of the parent soda (for partner sodas)
    isDeleted?: boolean; // Indicates if the soda record is soft deleted
    commissionSnapshot?: string | {
        Partners?: any | null;
        PartyCommission?: {
            ThirdPartyId?: number | null;
            TestPartyCommission?: number | null;
            OneDayPartyCommission?: number | null;
            SessionPartyCommission?: number | null;
            TestThirdPartyCommission?: number | null;
            OneDayThirdPartyCommission?: number | null;
            SessionThirdPartyCommission?: number | null;
            HarCut?: number | null;
        } | null;
    };
    gameType?: string;
    gameWiseSodaSequence?: number;
}

export interface IsFieldValid {
    isValid: boolean; 
    message: string;
}

export type AutoCompleteOption = {
  id: number
  name: string
  code?: string // Optional code field for displaying code + name
  displayName?: string // Optional formatted display name
}

export type PartyCode = {
  id: number
  partyCode: string
  partyName?: string
}

export enum InputType {
    TEXT = 'text',    
    NUMBER = 'number',
    PASSWORD = 'password',
    EMAIL = 'email',
    TEL = 'tel',
    DATE = 'date',  
    TIME = 'time',
    DATETIME_LOCAL = 'datetime-local',  
    URL = 'url',
    SEARCH = 'search',  
    COLOR = 'color',
    RANGE = 'range',    
    CHECKBOX = 'checkbox',
    RADIO = 'radio',    
    FILE = 'file',
    HIDDEN = 'hidden',  
    BUTTON = 'button',
    SUBMIT = 'submit',  
    RESET = 'reset',
    IMAGE = 'image',
    MONTH = 'month',
    WEEK = 'week',  
    TEXTAREA = 'textarea',
    SELECT = 'select',  
    MULTISELECT = 'multiselect',
    AUTOCOMPLETE = 'autocomplete',
    TOGGLE = 'toggle',      
    SLIDER = 'slider',
    SWITCH = 'switch'
}

export interface PartyFilters {
  partyCode: string;
  partyName: string;
  city: string;
  modifiedOnEnd?: string; 
  modifiedOnStart?: string;
};

export interface GameFilters {
  teamA: string;
  teamB?: string;
  teamC?: string;
  overs?: string;
  gameType?: string;
  modifiedOnEnd?: string; 
  modifiedOnStart?: string;
  createdOnStart?: string;
  createdOnEnd?: string;
};

export interface UserProfile {
  userName: string;
  fullName: string;
  firstName?: string;
  modifiedAt: Date;
}

export interface Game {
    id: number;
    gameType: string;
    teamA: string;
    teamB?: string;
    teamC?: string;
    overs?: number | null;
    createdBy?: User;
    createdOn?: Date | string;
    createdAt?: Date | string; // API might be returning this field
    modifiedBy?: User;
    modifiedOn?: Date | string;
    modifiedAt?: Date | string; // API might be returning this field
    /** True when the game already has a soda. Edit is hidden. */
    hasSodas?: boolean;
}

export interface Party {
  id: number;
  partyCode: string;
  partyName: string;
  phone: string;
  openingBalance: string;
  harCut?: number;
  currentBalance?: number; // Current balance from API
  createdOn?: string | Date; // Creation date from API
  modifiedBy?: User | string;
  modifiedOn?: string | Date;
  modifiedAt?: string | Date; // API might be returning this field
  city: string;
  /** When true, party is hidden on Trial Balance only (still usable elsewhere). */
  hideInGeneral?: boolean;
}

export interface PartnerSodaWinLoss {
  totalCredit: number;
  totalDebit: number;
  net: number;
}

export interface PartnerSodaSummaryItem {
  gameId: number;
  matchup: string;
  partnerId: number;
  partnerPartyCode: string;
  partnerPartyName: string;
  teamAWin: PartnerSodaWinLoss;
  teamBWin: PartnerSodaWinLoss;
  teamCWin?: PartnerSodaWinLoss | null;
  commissionNet?: number;
  partnerConfirmed?: boolean;
}

export interface PartnerSodaSummaryResponse {
  items: PartnerSodaSummaryItem[];
  totals: {
    teamA: PartnerSodaWinLoss;
    teamB: PartnerSodaWinLoss;
    teamC?: PartnerSodaWinLoss;
    commissionNet?: number;
  };
}

export interface RunningSodaByPartyRow {
  id: number;
  code: string;
  name: string;
  teamA: number;
  teamB: number;
  teamC?: number;
  commission: number;
  /** Session games: total K amount (same units as board soda.amount). */
  totalK?: number;
  /** Session games: total L amount (same units as board soda.amount). */
  totalL?: number;
  tallyConfirmed: boolean;
  isPartnerSoda?: boolean;
}

export interface RunningSodaByPartyResponse {
  gameId: number;
  gameType: string;
  teamA: string;
  teamB: string;
  teamC?: string;
  teamAName?: string;
  teamBName?: string;
  teamCName?: string;
  teams?: {
    teamA?: string;
    teamB?: string;
    teamC?: string;
  };
  teamAWin?: { net?: number };
  teamBWin?: { net?: number };
  teamCWin?: { net?: number };
  matchDate?: string;
  rows?: RunningSodaByPartyRow[];
  items?: RunningSodaByPartyRow[];
}

export interface PartnerSodaTransaction {
  id: number;
  sequence: number;
  entryCode: string;
  amount: number;
  rate: number;
  credit: number;
  debit: number;
  runningCredit: number;
  runningDebit: number;
  commission?: number;
  confirmed?: boolean;
}

export interface PartnerSodaTransactionsResponse {
  gameId: number;
  partnerId: number;
  partnerCode: string;
  partnerName: string;
  teamALabel: string;
  teamBLabel: string;
  teamCLabel?: string;
  totals: {
    teamA: number;
    teamB: number;
    teamC?: number;
    commission?: number;
  };
  transactions: PartnerSodaTransaction[];
}

export interface PartySodaTransactionDetail {
  id: number;
  /** Game-wide soda serial from API (`soda.gameWiseSodaSequence`). */
  gameWiseSodaSequence?: number;
  lk: string; // Entry code like "A/L", "A/K"
  /** Session: run number for display as runs/K or runs/L (from API or numeric firstChar). */
  runs?: number;
  amount: number;
  rate: number;
  teamAWin: number; // Win/loss for Team A
  teamBWin: number; // Win/loss for Team B
  teamCWin?: number; // Win/loss for Team C (if test match)
  runningTeamA: number; // Running total for Team A
  runningTeamB: number; // Running total for Team B
  runningTeamC?: number; // Running total for Team C (if test match)
  confirmed?: boolean;
  /** Team on the soda (normalized from API). */
  team?: string;
  commissionSnapshot?: Sodo['commissionSnapshot'];
  /** When set from API, overrides dialog `isPartnerSoda` for calculations. */
  isPartnerSoda?: boolean;
  /** Set when this row is a partner commission child; parent soda has no parent link. */
  parentId?: number;
  isDeleted?: boolean;
  createdAt?: string;
  modifiedAt?: string;
}

export interface PartySodaDetailResponse {
  gameId: number;
  partyId: number;
  partyCode: string;
  partyName: string;
  /** Party HarCut from `/api/Soda/detail` (Parties.HarCut). */
  harCut?: number;
  gameType: string;
  teamA: string;
  teamB: string;
  teamC?: string;
  matchDate?: string;
  partyTotalTeamA: number; // Total for party (Team A)
  partyTotalTeamB: number; // Total for party (Team B)
  partyTotalTeamC?: number; // Total for party (Team C)
  isConfirmed?: boolean;
  transactions: PartySodaTransactionDetail[];
  totals: {
    teamA: number;
    teamB: number;
    teamC?: number;
  };
  /** Session: match run used for run-wise win/loss (optional; falls back in UI). */
  sessionMatchRun?: number;
  /** Session: display name for winning side (optional; falls back in UI). */
  sessionWinnerTeamName?: string;
  /** Session: winning team or match run string from API. */
  winningTeam?: string;
  /** True when this party is its own partner. */
  isSelfPartner?: boolean;
  /** True when Games.IsGameOver is set. Sodas cannot be edited. */
  isGameOver?: boolean;
}

export interface Tenant {
  id: number;
  tenantName: string;
  role: string;
  databaseName: string;
  mobileNo: string;
  remarks: string;
  createdOn?: string;
  modifiedBy: string;
  modifiedAt: string;
  isActive: boolean;
  isDeleted: boolean;
  dataResetOn: string | null;
}

// API response might have different field names, so we'll map them
export interface ApiTenant {
  // ID variations
  id?: number;
  tenantId?: number;
  tenant_id?: number;
  
  // Name variations
  tenantName?: string;
  tenant_name?: string;
  name?: string;
  role?: string;
  
  // Database name variations
  databaseName?: string;
  database_name?: string;
  dbName?: string;
  
  // Mobile number variations
  mobileNo?: string;
  mobile_no?: string;
  mobile?: string;
  phoneNumber?: string;
  phone_number?: string;
  phone?: string;
  
  // Remarks variations
  remarks?: string;
  remark?: string;
  notes?: string;
  
  // Modified by variations
  modifiedBy?: string | number | null; // API returns number (user ID) or null
  modified_by?: string | number | null;
  modifiedByUser?: string | number | null;
  modifiedByName?: string | null; // API returns user name
  modified_by_name?: string | null;
  
  // Modified date variations
  modifiedOn?: string; // API returns modifiedOn
  modified_on?: string;
  modifiedAt?: string;
  modified_at?: string;
  updatedAt?: string;
  
  // Active status variations
  isActive?: boolean | number; // MySQL TINYINT(1) can be 0/1
  is_active?: boolean | number;
  active?: boolean | number;
  
  // Deleted status variations
  isDeleted?: boolean | number; // MySQL TINYINT(1) can be 0/1
  is_deleted?: boolean | number;
  deleted?: boolean | number;
  
  // Data reset date variations
  dataResetOn?: string | null;
  data_reset_on?: string | null;
  dataResetDate?: string | null;
  data_reset_date?: string | null;
  resetDate?: string | null;
  reset_date?: string | null;
  
  [key: string]: any; // Allow for additional fields
}
